import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { Data } from '@styled-icons/boxicons-regular';
import { getNode, select } from '@udecode/plate-core';
import { BaseRange, Path } from 'slate';
import { DreamBlock, IConnectionDataKey } from '@typedream/core';
import styled, { css } from 'styled-components';
import styles from '../../SideToolbar.module.css';

import { getTPlugin, useTPlateEditorRef } from '../../../../plate';
import { CollectionDataPluginOptions, IOptionsElement } from '../../../../nodes/collection/types';
import { ELEMENT_IMAGE, KEY_COLLECTION_CONNECT_DATA } from '../../../../pluginKeys';
import { selectElement } from '../../../../plugins/elementSelection/transforms';
import { BLOCK_ELEMENT_KEY } from '../../../../nodes/collection/components/CollectionDataElement/types';
import { CONNECTED_TYPES_OPTIONS_IMAGE, CONNECTED_TYPES_OPTIONS_TEXTS, LINK_TYPES } from '../../../../constants';

const DataIndicatorAccentStyle = css`
    color: var(--accent) !important;
`;

const DataIndicatorLighterStyle = css`
    color: var(--text-lighter) !important;
`;

const DataIndicatorWrapperDiv = styled.div<{ connected?:boolean }>`
    ${(props) => (props.connected ? DataIndicatorLighterStyle : DataIndicatorAccentStyle)}
`;

function ConnectedToDataIndicator({
  element,
  onClose,
  blockElementKey,
  path,
  containerClassnames,
}: {
  element: DreamBlock;
  onClose: () => void;
  blockElementKey: BLOCK_ELEMENT_KEY;
  path: Path;
  containerClassnames?: string
}) {
  const editor = useTPlateEditorRef();
  const { options } = getTPlugin<CollectionDataPluginOptions>(editor, KEY_COLLECTION_CONNECT_DATA);

  const {
    showSchema, setopenConnectData,
  } = options as CollectionDataPluginOptions;

  const [connectedToData, setconnectedToData] = useState<string[]>([]);
  const connected = useMemo(() => connectedToData?.includes(blockElementKey), [connectedToData]);
  const [selection, setselection] = useState<BaseRange | null>(null);

  useEffect(() => {
    let e = element as IOptionsElement;
    if (selection && (!LINK_TYPES.includes(element?.type as string))) {
      try {
        e = getNode(editor, selection.focus.path) as IOptionsElement;
      } catch (err) {
        return;
      }
    }

    const connectedToDataItems: string[] = [];
    e?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS]?.forEach(
      (i: IConnectionDataKey) => {
        if (!!i.blockElementKey && !!i.schemaKey) {
          connectedToDataItems.push(i.blockElementKey);
        }
      },
    );

    setconnectedToData(e?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA]
      ? connectedToDataItems : []);
  }, [
    element,
    element?.[BLOCK_ELEMENT_KEY.COLLECTION_CONNECT_DATA_KEYS],
    editor.selection,
    selection,
  ]);

  useEffect(() => {
    if (editor.selection) {
      setselection(editor.selection);
    }
  }, [editor.selection]);

  const connectedTypes = element?.type === ELEMENT_IMAGE
    ? CONNECTED_TYPES_OPTIONS_IMAGE : CONNECTED_TYPES_OPTIONS_TEXTS;

  return (showSchema ? (
    <div
      className={classNames('p-1 pl-2 pb-0 has-text-left is-size-7', containerClassnames)}
      contentEditable={false}
      style={{
        userSelect: 'none',
      }}
    >
      <div className={classNames(
        'is-flex is-align-items-center',
        {
          [styles.dataIndicatorLighter]: !connected,
          [styles.dataIndicatorAccent]: !!connected,
        },
      )}
      >
        <Data
          size={16}
          className={classNames('toolbar-connect-data-trigger mr-1')}
        />
        {connected ? 'Connected to Data' : 'Not Connected to Data'}
      </div>

      <DataIndicatorWrapperDiv
        connected={connected}
        className={classNames('with-cursor-pointer', {
          [styles.dataIndicatorLighter]: connected,
          [styles.dataIndicatorAccent]: !connected,
        })}
        role="none"
        onClick={(e) => {
          e.stopPropagation();
          if (selection) {
            select(editor, selection);
          } else if (path) {
            selectElement(editor, path);
          }
          onClose();
          setopenConnectData?.(true);
        }}
      >
        {connected ? 'Update data connection' : `Get ${(connectedTypes as any)?.[blockElementKey]?.title} from Data`}
        &rarr;
      </DataIndicatorWrapperDiv>
    </div>
  ) : <div />);
}

ConnectedToDataIndicator.defaultProps = {
  containerClassnames: '',
};

export default ConnectedToDataIndicator;
